import _ from "lodash";
import React, { useState, useContext, useEffect } from "react";
import { Button, Col, Input, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";
import { DesktopContainer } from "../../layouts";
import { useGetSpecificationItemsWithSamples, useGetVendorList } from "../../hooks";
import { ColPrompt, DebounceForText, TabAdditionalInfo, TabIdentification, TabProperties, TabTesting, } from "./";
import Select from "react-select";
import { api, constants, dateHelpers, reportHelpers, UserContext, } from "../../utils";
import { toast } from "react-toastify";
import { ItemTypeahead } from "../";
import { useSpecificationContextUpdater } from "./useSpecificationContextUpdater";
import { useSpecificationContextState } from "./useSpecificationContextState";
import classNames from "classnames";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { confirmAlert } from "react-confirm-alert";

const { ROLE_IDS } = constants;
const adminPowerQual = [
   ROLE_IDS.Admin,
   ROLE_IDS.QualityEdit,
   ROLE_IDS.PowerUser,
];

export default function QualitySpecifications() {
   const SpecificationItemsWithSamples = useGetSpecificationItemsWithSamples();
   const Vendors = useGetVendorList();
   const navigate = useNavigate();
   const { qualitySelect } = useParams();
   const location = useLocation();
   const SpecContextValue = useSpecificationContextState();
   const SpecContextUpdater = useSpecificationContextUpdater();
   const cancelToken = axios.CancelToken;
   const source = cancelToken.source();
   const { currentUser } = useContext(UserContext);
   const [isSaving, setIsSaving] = useState(false);
   const [activeTab, setActiveTab] = useState("1");
   const { list } = SpecificationItemsWithSamples;
   const { item1, item2 } = list;

   const roles = !_.isNil(currentUser)
      ? _.map(currentUser.roles, (r) => r.typeOfUserRole)
      : [];

   const EDITING_ALLOWED = !_.chain(roles)
      .intersectionWith(adminPowerQual, _.isEqual)
      .isEmpty()
      .value();

   useEffect(() => {
      if (_.isNil(qualitySelect)) {
         return;
      } else {
         const json = decodeURI(qualitySelect);
         const opt = JSON.parse(json);
         SpecContextUpdater.onSelectChange(opt);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [location]);

   const DeleteSpec = () => {
      confirmAlert({
         title: "Permanently Delete Specification?",
         message: `This will permanently delete specification with itemNumber: ${SpecContextValue.specification.itemNumber}. Are you sure?`,
         buttons: [
            {
               label: "Ok",
               onClick: () => {
                  api
                     .post(
                        `Quality/DeleteSpecification/${SpecContextValue.specification.itemNumber}`
                     )
                     .then((response) => {
                        if (response.data.success) {
                           toast.success("Successfully deleted the specification");
                        } else {
                           toast.error(response.data.message);
                        }
                     })
                     .catch(api.catchHandler);
               },
            },
            {
               label: "Cancel",
            },
         ],
      });
   };

   function DisplaySaveOrReport() {
      return (
         <Row style={{ paddingBottom: "30px" }}>
            <Col className="d-flex justify-content-end">
               <Button
                  children={"Save Specification"}
                  className="shredButton"
                  onClick={() => SaveSpecification()}
               />
               <Button
                  children={"Print Specification"}
                  className="mx-4 shredButton"
                  onClick={() =>
                     reportHelpers.downloadSpecReportPdf(
                        _.trim(SpecContextValue.specification.itemNumber)
                     )
                  }
               />
               <Button
                  children={"Cancel"}
                  className="me-4 shredButton"
                  onClick={() => SpecContextUpdater.onSelectChange(null)}
               />
            </Col>
         </Row>
      );
   }

   const findSpec = (itemOrSpec) => {
      let item = { label: "--- Select ---", value: "--- Select ---" };
      let spec = { label: "--- Select ---", value: "--- Select ---" };
      if (item1 && item1.length) {
         //find in the item list the current item
         let possibleItem = _.find(item1, (x) => x.value === SpecContextValue.specification.specNumber);
         if (!_.isNil(possibleItem)) item = possibleItem;
      }
      if (item2 && item2.length) {
         let possibleSpec = _.find(item2, (x) => x.value === SpecContextValue.specification.specNumber);
         if (!_.isNil(possibleSpec)) spec = possibleSpec;
      }
      if (itemOrSpec) return item;
      return spec;
   };

   const findVendor = () => {
      let vendor = { label: "--- Select ---", value: "--- Select ---" };
      if (Vendors.success && Vendors.list.length) {
         let possibleVendor = _.find(Vendors.list, (x) => x.value === SpecContextValue.specification.appSuppliers);
         if (!_.isNil(possibleVendor)) vendor = possibleVendor;
      }
      return vendor;
   };

   const onSelect = (propertyName, opt) => {
      if (opt && opt?.value) {
         SpecContextUpdater.onChange(propertyName, opt.value);
      }
   }; // this on change is for the rest of the selects

   const onSelectSpec = (opt) => {
      if (opt && opt?.value) {
         SpecContextUpdater.onSelectChange(opt);
      }
   }; // this on change will reload the entire page

   const runValidations = (spec) => {
      let warnings = [];
      function checkNil(propertyNames, checkBox = -1) {
         if (checkBox !== -1 && checkBox === false) return;
         _.forEach(propertyNames, function (value) {
            if (typeof spec[value] === "string") {
               if (_.isEmpty(spec[value])) {
                  warnings.push(`${value} is required and cannot be left blank`);
                  toast.error(`${value} is required and cannot be left blank`);
                  return false; // Breaks the forEach
               }
            } else {
               if (_.isNil(spec[value])) {
                  warnings.push(`${value} is required and cannot be left blank`);
                  toast.error(`${value} is required and cannot be left blank`);
                  return false; // Breaks the forEach
               }
            }
         });
      }
      if (!spec.isChemClass && !spec.isPkgClass) {
         checkNil(["yarnSizeUnitOfMeasure","yarnSizeNom", "yarnSizeMin", "yarnSizeMax", "numEnds", "filamentCount", "strengthNom", "strengthMin", "tenacityNom", "tenacityMin"]);
         checkNil(["elongNom", "elongMin", "elongMax"], spec.calElong);
         checkNil(["easlNom", "easlMin", "easlMax"], spec.calEASL);
         checkNil(["lase05Nom", "lase05Min", "lase05Max"], spec.calLase05);
         checkNil(["lase10Nom", "lase10Min", "lase10Max"], spec.calLase10);
         checkNil(["modulusNom", "modulusMin"], spec.calModulus);
         checkNil(["tensileNom", "tensileMin"], spec.calTensile);
         checkNil(["firstTwistNom", "firstTwistMin", "firstTwistMax", "firstTwistDirection"], spec.calFirstTwist);
         checkNil(["secondTwistNom", "secondTwistMin", "secondTwistMax", "secondTwistDirection"], spec.calSecondTwist);
         checkNil(["shrinkNom", "shrinkMin", "shrinkMax"], spec.calShrinkage);
         checkNil(["finishNom", "finishMin", "finishMax"], spec.calFinish);
         checkNil(["gaugeNom", "gaugeMin", "gaugeMax"], spec.calGauge);
         // Max is required wherever the property has one (QualityService.SaveSpecification enforces the same) - only
         // breaking strength, tenacity, Young's modulus and tensile modulus have no Max.
         checkNil(["absorbCapacityNom", "absorbCapacityMin", "absorbCapacityMax"], spec.calAbsorbCapacity);
         checkNil(["absorbSpeedNom", "absorbSpeedMin", "absorbSpeedMax"], spec.calAbsorbSpeed);
         checkNil(["shoreDurometerNom", "shoreDurometerMin", "shoreDurometerMax"], spec.calShoreDurometer);
         checkNil(["swellingFactorNom", "swellingFactorMin", "swellingFactorMax"], spec.calSwellingFactor);
         checkNil(["packDiaNom", "packDiaMin", "packDiaMax"], spec.calPackDiameter);
         checkNil(["packLbsNom", "packLbsMin", "packLbsMax"], spec.calPackWeight);
         checkNil(["packTraverse", "maxSplices", "transferTail"]);
      }
      else if (spec.isChemClass) {
         checkNil(["viscosityNom", "viscosityMin", "viscosityMax"], spec.calViscosity);
         checkNil(["phNom", "phMin", "phMax"], spec.calPH);
         checkNil(["solidsNom", "solidsMin", "solidsMax"], spec.calSolids);
         checkNil(["refractiveIdxNom", "refractiveIdxMin", "refractiveIdxMax"], spec.calRefrIdx);
         checkNil(["specificGravityNom", "specificGravityMin", "specificGravityMax"], spec.calSpecGrav);
         checkNil(["appearance"]);
      }
      else {
         checkNil(["pkgLengthNom", "pkgLengthMin", "pkgLengthMax"], spec.calPkgLength);
         checkNil(["pkgWidthNom", "pkgWidthMin", "pkgWidthMax"], spec.calPkgWidth);
         checkNil(["pkgHeightNom", "pkgHeightMin", "pkgHeightMax"], spec.calPkgHeight);
         checkNil(["pkgInnerDiaNom", "pkgInnerDiaMin", "pkgInnerDiaMax"], spec.calPkgInnerDia);
         checkNil(["pkgOuterDiaNom", "pkgOuterDiaMin", "pkgOuterDiaMax"], spec.calPkgOuterDia);
         checkNil(["pkgBaseDiaNom", "pkgBaseDiaMin", "pkgBaseDiaMax"], spec.calPkgBaseDia);
         checkNil(["pkgTipDiaNom", "pkgTipDiaMin", "pkgTipDiaMax"], spec.calPkgTipDia);
         checkNil(["pkgWallNom", "pkgWallMin", "pkgWallMax"], spec.calPkgWall);
         checkNil(["pkgMaterialType"], spec.calPkgMaterialType);
         checkNil(["pkgWeightRating"], spec.calPkgWeightRating);
         checkNil(["calPkgOther"], spec.calPkgOther);
      }
      if (_.isEmpty(warnings)) return false;
      return true; // there was an issue
   };

   const SaveSpecification = () => {
      if (!EDITING_ALLOWED) {
         toast.error("Sorry your user account does not have permissions to save quality specifications");
         return;
      }
      if (isSaving) return;
      const payload = _.cloneDeep(SpecContextValue.specification);
      let validated = runValidations(payload);
      if (validated) return;
      setIsSaving(true);

      api
         .postS("Quality/SaveQualitySpecification", payload, source.token)
         .then((r) => {
            if (r.data.success) {
               toast.success("Saved Specification Successfully");
               SpecContextUpdater.refreshSpecs();
            } else {
               toast.error(r.data.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setIsSaving(false);
         });
   };

   const date = SpecContextValue.specification?.revDate
      ? dateHelpers.toLongDateString(SpecContextValue.specification?.revDate)
      : null;

   return (
      (document.title = `Quality Specifications`),
      (
         <DesktopContainer screenName={"Quality Specifications"}>
            <div
               className={classNames("filter-header stick-top mb-3", {
                  "d-none": !_.isNil(SpecContextValue.specification),
               })}
            >
               <Row>
                  <Col xs="3" style={{ textAlign: "right", color: "white" }}>
                     <h5>Edit Specification</h5>
                  </Col>
               </Row>
               <hr />
               <Row className="mt-3">
                  <Col xs="3" style={{ textAlign: "right", marginRight: "5px", color: "white" }}>
                     Item #:
                  </Col>
                  <Col xs="6" style={{ paddingRight: "0px" }}>
                     <ItemTypeahead
                        value={SpecContextValue.selectedSpecification}
                        onChange={(opt) => onSelectSpec(opt)}
                        filterItemBy={constants.TYPE_AHEAD_CLASSES.SpecItemsWithAllActiveSamples}
                        includeSampleItems={true}
                     />
                  </Col>
               </Row>
               {EDITING_ALLOWED && (
                  <Row className="mt-3">
                     <Col xs="3" />
                     <Col>
                        <Button
                           className="shredButton"
                           style={{ marginRight: "15px" }}
                           onClick={() => DeleteSpec()}
                           disabled={_.isNil(SpecContextValue.specification)}
                        >
                           DELETE SPECIFICATION
                        </Button>
                     </Col>
                  </Row>
               )}
            </div>

            {SpecificationItemsWithSamples.success &&
               Vendors.success &&
               SpecContextValue.specification && (
                  <div>
                     <div className="filter-header p-3">
                        <Row>
                           <ColPrompt
                              sizePRP={1}
                              title={"Item:"}
                              alignPRP={"right"}
                              classNamePRP="px-0 pt-2"
                           />
                           <Col
                              xl={{ size: 2 }}
                              xxl={{ size: 3 }}
                              children={
                                 <Select
                                    classNamePrefix="serviceThreadSelect"
                                    options={item1}
                                    value={findSpec(true)}
                                    onChange={(opt) => onSelectSpec(opt)}
                                 />
                              }
                           />

                           <ColPrompt
                              sizePRP={{ size: 1, offset: 1 }}
                              title={"Rev:"}
                              alignPRP={"right"}
                              classNamePRP="px-0 pt-2"
                           />
                           <DebounceForText
                              valuePRP={SpecContextValue.specification.revNumber}
                              readOnlyPRP={false}
                              sizePRP={1}
                           />

                           <ColPrompt
                              sizePRP={1}
                              title={"Rev Date:"}
                              alignPRP={"right"}
                              classNamePRP="px-0 pt-2"
                           />
                           <DebounceForText
                              valuePRP={date}
                              readOnlyPRP={false}
                              sizePRP={1}
                              inputStyle={{ width: "120px" }}
                           />

                           <ColPrompt
                              sizePRP={1}
                              title={"QC HOLD:"}
                              alignPRP={"right"}
                              classNamePRP="px-0 pt-2"
                           />
                           <Col
                              className="pt-2"
                              size={{ size: 1 }}
                              children={
                                 <Input
                                    type="checkbox"
                                    checked={SpecContextValue.specification.onHold}
                                    disabled
                                 />
                              }
                           />

                           <Col
                              xxl={{ size: 1 }}
                              children={<h5>View BOM</h5>}
                              className={classNames("shredLink pt-2", {
                                 "d-none":
                                    SpecContextValue.specification.isPurchasedMaterialClass,
                              })}
                              onClick={() =>
                                 navigate(constants.ENDPOINTS.bomComponents, {
                                    state: findSpec(true),
                                    replace: true,
                                 })
                              }
                           />
                        </Row>

                        <Row className="my-3">
                           <ColPrompt
                              sizePRP={1}
                              title={"Spec:"}
                              alignPRP={"right"}
                              classNamePRP="px-0 pt-2"
                           />
                           <Col
                              xl={{ size: 2 }}
                              xxl={{ size: 3 }}
                              children={
                                 <Select
                                    classNamePrefix="serviceThreadSelect"
                                    options={item2}
                                    value={findSpec(false)}
                                    onChange={(opt) => onSelectSpec(opt)}
                                 />
                              }
                           />

                           <ColPrompt
                              sizePRP={{ size: 1, offset: 1 }}
                              title={"App Suppliers:"}
                              alignPRP={"right"}
                              classNamePRP="px-0 pt-2"
                           />
                           <Col
                              xl={{ size: 2 }}
                              xxl={{ size: 3 }}
                              children={
                                 <Select
                                    classNamePrefix="serviceThreadSelect"
                                    className={classNames("", {
                                       "d-none":
                                          !SpecContextValue.specification
                                             .isPurchasedMaterialClass,
                                    })}
                                    options={Vendors.list}
                                    value={findVendor()}
                                    onChange={(opt) => onSelect("appSuppliers", opt)}
                                 />
                              }
                           />

                           <ColPrompt
                              sizePRP={1}
                              title={"Heightened:"}
                              alignPRP={"right"}
                              classNamePRP="px-0 pt-2"
                           />
                           <Col
                              className="pt-2"
                              size={{ size: 1 }}
                              children={
                                 <Input
                                    type="checkbox"
                                    checked={SpecContextValue.specification.heightened}
                                    onChange={() => SpecContextUpdater.onChange("heightened", !SpecContextValue.specification.heightened)}
                                 />
                              }
                           />

                           <ColPrompt
                              sizePRP={1}
                              title={"Tightened:"}
                              alignPRP={"right"}
                              classNamePRP="px-0 pt-2"
                           />
                           <Col
                              className="pt-2"
                              size={{ size: 1 }}
                              children={
                                 <Input
                                    type="checkbox"
                                    checked={SpecContextValue.specification.tightened}
                                    onChange={() => SpecContextUpdater.onChange("tightened", !SpecContextValue.specification.tightened)}
                                 />
                              }
                           />
                        </Row>

                        <DisplaySaveOrReport />
                     </div>
                     {/* <hr></hr> */}
                     <div
                        className="mt-3 filter-header"
                        style={{ paddingBottom: "0px" }}
                     >
                        <Nav tabs className="d-flex justify-content-around">
                           <NavItem>
                              <NavLink
                                 className={activeTab === "1" ? "active" : "inactive"}
                                 onClick={() => setActiveTab("1")}
                              >
                                 Properties
                              </NavLink>
                           </NavItem>
                           <NavItem>
                              <NavLink
                                 className={activeTab === "2" ? "active" : "inactive"}
                                 onClick={() => setActiveTab("2")}
                              >
                                 Identification
                              </NavLink>
                           </NavItem>
                           <NavItem>
                              <NavLink
                                 className={activeTab === "3" ? "active" : "inactive"}
                                 onClick={() => setActiveTab("3")}
                              >
                                 Testing
                              </NavLink>
                           </NavItem>
                           <NavItem>
                              <NavLink
                                 className={activeTab === "4" ? "active" : "inactive"}
                                 onClick={() => setActiveTab("4")}
                              >
                                 Additional Info
                              </NavLink>
                           </NavItem>
                        </Nav>
                        <TabContent activeTab={activeTab}>
                           <TabPane tabId="1" children={<TabProperties />} />
                           <TabPane tabId="2" children={<TabIdentification />} />
                           <TabPane tabId="3" children={<TabTesting />} />
                           <TabPane tabId="4" children={<TabAdditionalInfo />} />
                        </TabContent>
                        <div className="bg-black-blue">
                           <DisplaySaveOrReport />
                        </div>
                     </div>
                  </div>
               )}
         </DesktopContainer>
      )
   );
}
